/**
 * calculateEI.js
 * Calcul du revenu net avant PAS pour le statut Entreprise Individuelle (EI au régime réel)
 * Cotisations sociales réelles (TNS) calculées en local via le modèle social officiel.
 */

import { evaluateMonEntreprise } from './monEntrepriseClient';

// ========== MAPPING MÉTIER → TYPE D'ACTIVITÉ ==========
const METIER_TO_ACTIVITE_NATURE = {
  "Commerce (achat, revente)": "'commerciale'",
  "Bâtiment, travaux": "'artisanale'",
  "Création artisanale": "'artisanale'",
  "Production alimentaire, culinaire": "'artisanale'",
  "Prestation de services (conseil, bureau d'études, communication, etc.)": "'libérale'",
  "Informatique, numérique": "'libérale'",
  "Formation professionnelle": "'libérale'",
  "Culturelle, artistique": "'libérale'",
  "Agriculture, paysage": "'artisanale'",
  "Service à la personne": "'libérale'",
  "Bien-être, soin": "'libérale'",
};

// ========== CALCUL DES CATÉGORIES DE CHARGES ==========
const MIN_STRUCTURE = {
  logiciel_compta: 20, compte_bancaire: 10, assurance_rcp: 15,
  assurance_decennale: 250, comptable: 250, cet_cvae_cfe: 27, mutuelle_prevoyance: 75,
};

function computeCategories(charges, metier, ca_ht, coeff) {
  const isBatiment    = metier === 'Bâtiment, travaux';
  const isAlimentaire = metier === 'Production alimentaire, culinaire';
  const isFormation   = metier === 'Formation professionnelle';
  const M = MIN_STRUCTURE;
  const r2 = (n) => Math.round(n * 100) / 100;

  const frais_fonctionnement = r2(
    ((charges.loyer || 0) +
    (charges.internet_telephone || 0) * 0.5 +
    (charges.indemnites_km || 0) +
    (charges.deplacements || 0) +
    (charges.communication_pub || 0) +
    (charges.consommables || 0) +
    (charges.repas_affaires || 0)) / coeff
  );

  const label_bio = isAlimentaire && (charges.label_bio || 0) > 0
    ? Math.max((charges.label_bio || 0) / coeff, (160 + 180) / 12) : 0;
  const code_barres = isAlimentaire ? (charges.code_barres || 0) / coeff : 0;
  const logiciel_formation = isFormation
    ? Math.max((charges.logiciel_formation || 0) / coeff, ca_ht * 0.03) : 0;
  const logiciel_compta = Math.max((charges.logiciel_compta || 0) / coeff, M.logiciel_compta);
  const compte_bancaire = Math.max((charges.compte_bancaire || 0) / coeff, M.compte_bancaire);
  const rcp = isBatiment ? 0 : Math.max((charges.assurance_rcp || 0) / coeff, M.assurance_rcp);
  const decennale = isBatiment ? Math.max((charges.assurance_decennale || 0) / coeff, M.assurance_decennale) : 0;
  const comptable = Math.max((charges.comptable || 0) / coeff, M.comptable);
  const cet = Math.max((charges.cet_cvae_cfe || 0) / coeff, M.cet_cvae_cfe);
  const mutuelle = Math.max((charges.mutuelle_prevoyance || 0) / coeff, M.mutuelle_prevoyance);

  const autres_frais_structure = r2(
    label_bio + code_barres + logiciel_formation + logiciel_compta +
    compte_bancaire + rcp + decennale + comptable + cet + mutuelle
  );

  return { frais_fonctionnement, autres_frais_structure };
}

/**
 * Calcule le revenu net avant PAS pour le statut EI (Régime réel)
 *
 * En EI au régime réel, toutes les charges professionnelles sont déductibles.
 * Les cotisations sociales (TNS) se calculent sur le bénéfice via le modèle social.
 *
 * @param {number} ca_ht - Chiffre d'affaires HT mensuel
 * @param {object} charges - Charges professionnelles déductibles
 * @param {string} metier - Métier exercé
 * @returns {Promise<object>} Détails complets du calcul
 */
export async function calculateEI(ca_ht, isTTC, charges, metier = '') {
  if (!ca_ht || ca_ht < 0) {
    throw new Error('Le chiffre d\'affaires doit être un nombre positif');
  }
  if (!charges || typeof charges !== 'object') {
    throw new Error('Les charges doivent être un objet valide');
  }

  const coeff = isTTC ? 1.2 : 1;

  // ========== ÉTAPE 1 : CHARGES ET MARGES ==========
  const categories = computeCategories(charges, metier, ca_ht, coeff);

  const matieres_premieres_saisies = (charges.matieres_premieres || 0) / coeff;
  const matieres_premieres = matieres_premieres_saisies > 0
    ? matieres_premieres_saisies
    : (metier === 'Bâtiment, travaux' ? ca_ht * 0.45 : 0);
  const sous_traitance = (charges.sous_traitance || 0) / coeff;
  const marge_brute = ca_ht - matieres_premieres - sous_traitance;

  const marge_economique = Math.max(0,
    marge_brute - categories.autres_frais_structure - categories.frais_fonctionnement
  );

  // ========== ÉTAPE 2 : COTISATIONS (modèle social local) ==========
  const activite_nature = METIER_TO_ACTIVITE_NATURE[metier] || "'libérale'";
  const marge_economique_annuelle = marge_economique * 12;

  const activiteFlags =
    activite_nature === "'libérale'"
      ? {
          "entreprise . activité . nature": "'libérale'",
          "entreprise . activité . nature . libérale . réglementée": "non"
        }
      : activite_nature === "'artisanale'"
      ? {
          "entreprise . activité . nature": "'artisanale'"
        }
      : activite_nature === "'commerciale'"
      ? {
          "entreprise . activité . nature": "'commerciale'"
        }
      : {};

  const [remuneration_nette_api] = await evaluateMonEntreprise(
    ["dirigeant . rémunération . net"],
    {
      "entreprise . chiffre d'affaires": {
        valeur: String(Math.round(marge_economique_annuelle)),
        unité: '€/an',
      },
      "entreprise . catégorie juridique": "'EI'",
      "entreprise . catégorie juridique . EI . auto-entrepreneur": "non",
      ...activiteFlags
    }
  );

  const remuneration_nette_avant_pas = remuneration_nette_api / 12;
  const cotisations_sociales = marge_economique - remuneration_nette_avant_pas;
  const taux_cotisations = marge_economique > 0
    ? (cotisations_sociales / marge_economique) * 100
    : 0;

  const arrondir = (n) => Math.round(n * 100) / 100;

  return {
    source: 'api',
    remuneration_nette_avant_pas: arrondir(remuneration_nette_avant_pas),

    ca_ht: arrondir(ca_ht),
    marge_brute: arrondir(marge_brute),
    marge_economique: arrondir(marge_economique),
    benefice: arrondir(marge_economique),

    taux_cotisations: arrondir(taux_cotisations),
    cotisations_sociales: arrondir(cotisations_sociales),

    categories_charges: {
      matieres_et_sous_traitance: arrondir(matieres_premieres + sous_traitance),
      frais_participation_coop: 0,
      autres_frais_structure: categories.autres_frais_structure,
      frais_fonctionnement: categories.frais_fonctionnement,
    },

    statut: 'EI',
    description: 'Entreprise Individuelle au régime réel',

    protection_sociale: {
      assurance_chomage: false,
      retraite_complete: false,
      retraite_base: true,
      retraite_complementaire: true,
      accident_travail: false,
      maladie_professionnelle: false,
      formation_professionnelle: true,
      indemnites_journalieres: true,
    },

    avantages: [
      "Toutes les charges sont déductibles",
      "Pas de capital social minimum",
      "Comptabilité simplifiée par rapport à une société",
    ],
    inconvenients: [
      "Pas d'assurance chômage",
      "Protection sociale limitée (TNS)",
      "Cotisations calculées sur le bénéfice",
      "Responsabilité illimitée",
    ],
  };
}

export default calculateEI;
